// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  mdiAccount,
  mdiAccountMultiple,
  mdiCheck,
  mdiClose,
  mdiContentSavePlus,
  mdiDomain
} from "@mdi/js";
import { Button, Input, Select } from "antd";
import { selectIsTypeWellSavable, useForecastStore } from "stores/forecast";
import styled from "styled-components";

import { ProjectWithPermission } from "models/projects";

import { Tooltip } from "components/base";
import {
  useSelectedProject,
  useSelectedProjectPermissions
} from "components/project/projects/hooks";
import { useProjectListQuery } from "components/project/projects/queries";

import { useTypeWellSaveAs } from "./hooks/useTypeWellSaveAs";

const { Option } = Select;

interface TypeWellSaveAsProps {
  isOpen: boolean;
  selectedTypeWellTitle?: string;
  onCancel: () => void;
}

export function TypeWellSaveAs({
  isOpen,
  selectedTypeWellTitle = "",
  onCancel
}: TypeWellSaveAsProps) {
  const { selectedProject } = useSelectedProject();
  const projectPermissions = useSelectedProjectPermissions();

  const [saveAsTitle, setSaveAsTitle] = useState("");
  const [saveAsToProjectId, setSaveAsToProjectId] = useState<string>(
    selectedProject?.projectId
  );
  const [projects, setProjects] = useState<ProjectWithPermission[]>([]);

  const saveAsInputRef = useRef<HTMLInputElement>(null);

  const { handleSave, isCloning, hasWellDataError, updateOriginalRefs } =
    useTypeWellSaveAs({
      selectedProject,
      saveAsToProjectId,
      saveAsTitle,
      onSuccess: onCancel
    });

  const isTypeWellSavable = useForecastStore(selectIsTypeWellSavable);

  useProjectListQuery({
    onSuccess: (projects) => {
      const newProjects = projects.data.map((project) => ({
        ...project,
        permission: project.permission || projectPermissions
      }));
      setProjects(newProjects);
    }
  });

  useEffect(() => {
    if (isOpen) {
      setSaveAsTitle(`${selectedTypeWellTitle} Copy`);
      updateOriginalRefs();

      // Select input text on next tick to ensure DOM is ready
      requestAnimationFrame(() => {
        saveAsInputRef.current?.select();
      });
    }
  }, [isOpen, selectedTypeWellTitle, updateOriginalRefs]);

  useEffect(() => {
    if (!projects?.length) return;

    const editableProjects = projects.filter((proj) => proj.permission?.canEditTypeWells);
    const isCurrentProjectEditable = editableProjects.some(
      (proj) => proj.projectId === saveAsToProjectId
    );

    if (!isCurrentProjectEditable && editableProjects.length > 0) {
      setSaveAsToProjectId(editableProjects[0].projectId);
    }
  }, [projects, saveAsToProjectId]);

  const handleCancel = useCallback(() => {
    setSaveAsTitle(`${selectedTypeWellTitle} Copy`);
    onCancel();
  }, [selectedTypeWellTitle, onCancel]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSave();
      } else if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  const editableProjects = ((projects as ProjectWithPermission[]) ?? []).filter(
    (proj) => proj.permission?.canEditTypeWells
  );

  return (
    <SaveAsWrapper>
      <SaveAsInput
        ref={saveAsInputRef}
        value={saveAsTitle}
        onChange={(e) => setSaveAsTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Enter type well name"
      />
      <Select
        data-testid="type-well-save-as-project-selector"
        value={saveAsToProjectId}
        onChange={(val) => setSaveAsToProjectId(val)}
        placeholder="Select project">
        {editableProjects.map((project) => (
          <Option
            key={project.projectId}
            value={project.projectId}
            label={project.projectName}>
            <OptionWrapper>
              <span role="img">
                {project.projectType === "Personal" ? (
                  <Icon path={mdiAccount} size={1.1} />
                ) : project.projectType === "Shared" ? (
                  <Icon path={mdiAccountMultiple} size={1.1} />
                ) : (
                  <Icon path={mdiDomain} size={1.1} />
                )}
              </span>
              {project.projectName}
            </OptionWrapper>
          </Option>
        ))}
      </Select>
      <Tooltip title="Cancel">
        <Button
          icon={<Icon path={mdiClose} size={1} color="#bbbbbb" />}
          type="text"
          onClick={handleCancel}
        />
      </Tooltip>
      <Tooltip title="Save As">
        <Button
          data-testid="type-well-save-as-confirm-button"
          icon={
            isTypeWellSavable ? (
              <Icon path={mdiContentSavePlus} size={1} color="#02c7b0" />
            ) : (
              <Icon path={mdiCheck} size={1} color="#02c7b0" />
            )
          }
          type="text"
          onClick={handleSave}
          disabled={!saveAsTitle.trim() || hasWellDataError || isCloning}
          loading={isCloning}
        />
      </Tooltip>
    </SaveAsWrapper>
  );
}
const SaveAsWrapper = styled.div`
  display: flex;
  align-items: center;
  vertical-align: middle;
  width: 100%;
  gap: 5px;
  overflow: hidden;
  padding-left: 2px;

  .ant-btn-icon-only {
    width: 24px;
    height: 24px;
  }

  .ant-select-selector {
    max-width: 125px;
    min-width: 125px;
  }
`;

const SaveAsInput = styled(Input)`
  height: 25px;
  border: 1px solid black;
  background-color: white;
  max-width: 330px;
  min-width: 330px;
`;

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  span {
    padding: 2px 5px 0;
  }
`;

export default TypeWellSaveAs;
